import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useDivisionFor } from '../../entities/pipeline';
import { divisionApi, type DivisionOverview } from '../../shared/api/division.api';
import { useCachedQuery } from '../../shared/lib/queryCache';
import { withCount } from '../../shared/lib/plural';
import { RefreshingHint } from '../../shared/ui/RefreshingHint';
import { MembersTab } from './MembersTab';
import { ProjectsTab } from './ProjectsTab';
import { GroupsTab } from './GroupsTab';

/**
 * Отделение глазами руководителя: состав, группы, направления.
 *
 * Три вкладки, а не три страницы: всё это одно решение — «как устроено моё
 * отделение», и принимают его за один заход, когда кого-то нанимают или
 * заводят новый паблик. Дневная текучка живёт отдельно, в «Сегодня».
 *
 * Здесь нет ни одной цифры выработки. Это намеренно: отчёт — соседний раздел,
 * а смешение «кто у меня работает» и «сколько они сделали» превращает экран
 * в панель, на которую смотрят вместо работы.
 */
export function DivisionPage(): JSX.Element {
  const { division, isLoading: contextLoading } = useDivisionFor('head');
  // Вкладка — в адресе (02.11.2026): «Назад» и ссылка открывают ту же.
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'projects' ? 'projects' : 'members';
  const setTab = (value: string) => setParams((current) => {
    const next = new URLSearchParams(current);
    if (value === 'members') next.delete('tab');
    else next.set('tab', value);
    return next;
  }, { replace: true });

  const key = `division:${division?.id}`;
  const { data, isLoading, isRefreshing, refresh } = useCachedQuery<DivisionOverview>(
    key,
    () => divisionApi.getOverview(division!.id),
    { enabled: Boolean(division?.id) },
  );

  // Изменение видно сразу (08.10.2026): правка ложится поверх данных, сервер
  // перечитывается в фоне. Раньше кэш сбрасывался, и на каждом переключателе
  // страница пропадала в скелетон и рисовалась заново.
  const [draft, setDraft] = useState<DivisionOverview | null>(null);
  useEffect(() => setDraft(null), [data]);
  function reload(): void {
    void refresh();
  }
  function patch(fn: (o: DivisionOverview) => DivisionOverview): void {
    setDraft((prev) => {
      const base = prev ?? data;
      return base ? fn(base) : prev;
    });
  }

  if (contextLoading || (isLoading && !data)) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!division) {
    return (
      <p className="text-muted-foreground">
        Вы не руководите ни одним отделением.
      </p>
    );
  }

  const overview = draft ?? data;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="font-medium">{overview?.division.name ?? division.name}</span>
        <span className="text-sm text-muted-foreground">
          {withCount(overview?.members.length ?? 0, 'человек', 'человека', 'человек')}
          {' · '}
          {withCount(overview?.projects.length ?? 0, 'направление', 'направления', 'направлений')}
        </span>
        <RefreshingHint active={isRefreshing} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="members">Состав</TabsTrigger>
          <TabsTrigger value="projects">Направления</TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-4">
          <MembersTab divisionId={division.id} overview={overview} onChanged={reload} onPatch={patch} />
        </TabsContent>
        <TabsContent value="groups" className="mt-4">
          <GroupsTab divisionId={division.id} overview={overview} onChanged={reload} />
        </TabsContent>
        <TabsContent value="projects" className="mt-4">
          <ProjectsTab divisionId={division.id} overview={overview} onChanged={reload} onPatch={patch} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
